import { useState, useEffect } from 'react';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { Button } from '@/components/ui/button';
import { Download, Menu, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { usePortfolioData } from '@/hooks/use-portfolio-data';

export function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { data: portfolioData, isLoading } = usePortfolioData();

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const NavLink = ({ href, children }: { href: string, children: React.ReactNode }) => (
    <a 
      href={href} 
      className="nav-link hover:text-primary transition-colors"
      onClick={closeMobileMenu}
    >
      {children}
    </a>
  );

  return (
    <nav className={cn(
      "fixed w-full bg-white/80 dark:bg-background/80 backdrop-blur-md z-50 transition-all duration-300",
      scrolled ? "shadow-md" : "shadow-sm"
    )}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          <a href="#hero" className="text-xl font-bold flex items-center space-x-2">
            <span className="text-primary">&lt;</span>
            <span className="bg-gradient-to-r from-primary to-violet-500 text-transparent bg-clip-text">DevPortfolio</span>
            <span className="text-primary">/&gt;</span>
          </a>
          
          {/* Desktop menu */}
          <div className="hidden md:flex items-center space-x-8">
            <NavLink href="#about">About</NavLink>
            <NavLink href="#skills">Skills</NavLink>
            <NavLink href="#projects">Projects</NavLink>
            <NavLink href="#experience">Experience</NavLink>
            <NavLink href="#contact">Contact</NavLink>
            
            <ThemeToggle />
            
            {!isLoading && portfolioData && (
              <a 
                href={portfolioData.resumeUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center"
              >
                <Button className="flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Resume
                </Button>
              </a>
            )}
          </div>
          
          {/* Mobile menu button */}
          <div className="md:hidden flex items-center space-x-2">
            <ThemeToggle />
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X /> : <Menu />}
            </Button>
          </div>
        </div>
      </div>
      
      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white dark:bg-background shadow-lg animate-in slide-in-from-top">
          <div className="px-4 py-2 space-y-3">
            <NavLink href="#about">About</NavLink>
            <NavLink href="#skills">Skills</NavLink>
            <NavLink href="#projects">Projects</NavLink>
            <NavLink href="#experience">Experience</NavLink>
            <NavLink href="#contact">Contact</NavLink>
            
            <div className="border-t border-gray-200 dark:border-gray-700 pt-2">
              {!isLoading && portfolioData && (
                <a 
                  href={portfolioData.resumeUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block w-full"
                >
                  <Button className="flex items-center gap-2 w-full">
                    <Download className="w-4 h-4" />
                    Resume
                  </Button>
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navbar;
